import { getCurrentUser } from "@/services/authService";
import type { Currency } from "@/lib/currencies/currenciesStore";
import { mapApiCurrency, DEFAULT_PKR_CURRENCY } from "@/lib/currencies/currenciesStore";

export type CompanyBranding = {
  name: string;
  logo: string | null;
  address_line?: string | null;
  email?: string | null;
  phone?: string | null;
  primary_color?: string | null;
  cnic_masking?: string | null;
  currency?: Currency | null;
  timezone?: string | null;
};

export type AuthUser = {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  /** Public disk URL when user has uploaded an avatar. */
  avatar_url?: string | null;
  /** Tenant scope for permission/role templates (null for typical super admins). */
  company_id?: number | null;
  /**
   * From GET `/api/v1/user` — tenant name + logo URL for sidebar/header.
   * Present when the user has a company_id (does not require `company_can_view`).
   */
  company_branding?: CompanyBranding | null;
  role?: string | null;
  roles?: string[] | null;
  /** Permission `name` strings (Spatie). */
  permissions: string[];
  email_verified_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export function isSuperAdmin(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  const role = (user.role ?? "").toLowerCase();
  if (role === "super-admin" || role === "super admin") return true;
  const roles = (user.roles ?? []).map((r) => (r ?? "").toLowerCase());
  if (roles.includes("super-admin")) return true;
  // Spatie role name from seeders (`Super Admin` → `super admin`).
  return roles.includes("super admin");
}

export function can(user: AuthUser | null | undefined, permissionName: string): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  return Boolean(user.permissions?.includes(permissionName));
}

export function normalizeAuthUser(payload: unknown): AuthUser | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as { data?: unknown; user?: unknown };
  const candidate = (record.data || record.user || payload) as Partial<AuthUser> & { permissions?: unknown; roles?: unknown };

  if (!candidate?.name || !candidate?.email) return null;

  const permissions =
    Array.isArray(candidate.permissions) ? candidate.permissions.filter((p): p is string => typeof p === "string") : [];
  const roles = Array.isArray(candidate.roles) ? candidate.roles.filter((r): r is string => typeof r === "string") : null;

  const companyRaw = (candidate as { company_id?: unknown }).company_id;
  const companyId =
    companyRaw === null || companyRaw === undefined || companyRaw === ""
      ? null
      : Number(companyRaw);

  const phoneRaw = (candidate as { phone?: unknown }).phone;
  const phone =
    phoneRaw === null || phoneRaw === undefined || phoneRaw === ""
      ? null
      : String(phoneRaw).trim() || null;

  const avatarRaw = (candidate as { avatar_url?: unknown }).avatar_url;
  const avatarUrl =
    avatarRaw === null || avatarRaw === undefined || avatarRaw === ""
      ? null
      : String(avatarRaw).trim() || null;

  let company_branding: CompanyBranding | null = null;
  const brandingRaw = (candidate as { company_branding?: unknown }).company_branding;
  if (brandingRaw && typeof brandingRaw === "object" && !Array.isArray(brandingRaw)) {
    const b = brandingRaw as {
      name?: unknown;
      logo?: unknown;
      address_line?: unknown;
      email?: unknown;
      phone?: unknown;
      primary_color?: unknown;
      cnic_masking?: unknown;
    };
    const bn = typeof b.name === "string" ? b.name : "";
    const bl =
      b.logo === null || b.logo === undefined || b.logo === "" ? null : String(b.logo).trim() || null;
    const addressLine =
      b.address_line === null || b.address_line === undefined || b.address_line === ""
        ? null
        : String(b.address_line).trim() || null;
    const email =
      b.email === null || b.email === undefined || b.email === "" ? null : String(b.email).trim() || null;
    const phone =
      b.phone === null || b.phone === undefined || b.phone === "" ? null : String(b.phone).trim() || null;
    const primaryColor =
      b.primary_color === null || b.primary_color === undefined || b.primary_color === ""
        ? null
        : String(b.primary_color).trim() || null;
    const cnicMasking =
      b.cnic_masking === null || b.cnic_masking === undefined || b.cnic_masking === ""
        ? null
        : String(b.cnic_masking).trim() || null;
    const timezone =
      b.timezone === null || b.timezone === undefined || b.timezone === ""
        ? null
        : String(b.timezone).trim() || null;
    let currencyObj: Currency | null = null;
    const curRaw = (b as { currency?: unknown }).currency;
    if (curRaw && typeof curRaw === "object" && !Array.isArray(curRaw)) {
      currencyObj = mapApiCurrency(curRaw as Parameters<typeof mapApiCurrency>[0]);
    }
    if (bn.trim() || bl) {
      company_branding = {
        name: bn,
        logo: bl,
        address_line: addressLine,
        email,
        phone,
        primary_color: primaryColor,
        cnic_masking: cnicMasking,
        currency: currencyObj,
        timezone,
      };
    }
  }

  return {
    id: Number(candidate.id || 0),
    name: String(candidate.name),
    email: String(candidate.email),
    phone,
    avatar_url: avatarUrl,
    company_id: Number.isFinite(companyId) && companyId > 0 ? companyId : null,
    company_branding,
    role: (candidate.role as string | null | undefined) ?? null,
    roles,
    permissions,
    email_verified_at: (candidate.email_verified_at as string | null | undefined) ?? null,
    created_at: (candidate.created_at as string | undefined) ?? "",
    updated_at: (candidate.updated_at as string | undefined) ?? "",
  };
}

export function getStoredAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("auth_user");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthUser;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Refresh `auth_user` from `/api/v1/user` so permission changes
 * apply without hard refresh.
 */
export async function refreshStoredAuthUser(): Promise<AuthUser | null> {
  try {
    const payload = await getCurrentUser();
    const user = normalizeAuthUser(payload);
    if (user) {
      localStorage.setItem("auth_user", JSON.stringify(user));
      window.dispatchEvent(new Event("auth_user_updated"));
      return user;
    }
    localStorage.removeItem("auth_user");
    window.dispatchEvent(new Event("auth_user_updated"));
    return null;
  } catch {
    // Keep existing cached value on network errors.
    return getStoredAuthUser();
  }
}

